import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import { colors } from '../../const/colors'

export interface BulkAction {
  label: string
  onClick: () => void
  variant?: 'primary' | 'danger'
  icon?: React.ReactNode
  disabled?: boolean
  e2e?: string
}

interface BulkActionsBarProps {
  count: number
  onClear: () => void
  actions: BulkAction[]
  /** 'danger' renders red border, red shadow, red count text */
  color?: 'default' | 'danger'
}

export function BulkActionsBar({ count, onClear, actions, color = 'default' }: BulkActionsBarProps) {
  const { t } = useTranslation('generic')

  if (count === 0) return null

  const isDanger = color === 'danger'
  const accentColor = isDanger ? colors.red : colors.navy
  const barShadow = isDanger ? colors.shadowBulkRed : colors.shadowBulk

  return (
    <Box
      data-e2e="bulk-actions-bar"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        background: colors.surface,
        border: `1.5px solid ${accentColor}`,
        borderRadius: colors.radiusBulkBar,
        p: '13px 18px',
        mb: '14px',
        boxShadow: barShadow,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        sx={{ fontWeight: 800, color: accentColor, fontSize: '14px', whiteSpace: 'nowrap' }}
      >
        {count} {t('selected')}
      </Typography>

      <Box sx={{ width: '1px', height: '20px', background: colors.border3, flexShrink: 0 }} />

      <Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap', flex: 1 }}>
        {actions.map(action => (
          <Box
            key={action.label}
            onClick={action.disabled ? undefined : action.onClick}
            data-e2e={action.e2e}
            aria-disabled={!!action.disabled}
            sx={
              action.disabled
                ? {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: colors.border2,
                    color: colors.slate,
                    fontWeight: 700,
                    fontSize: '13.5px',
                    px: '16px',
                    py: '9px',
                    borderRadius: colors.radiusBtn,
                    cursor: 'default',
                    userSelect: 'none',
                  }
                : action.variant === 'danger'
                ? {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: colors.red,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '13.5px',
                    px: '16px',
                    py: '9px',
                    borderRadius: colors.radiusBtn,
                    cursor: 'pointer',
                    userSelect: 'none',
                    boxShadow: colors.shadowBtnRed,
                    transition: 'background .15s',
                    '&:hover': { background: colors.redDark },
                  }
                : {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: colors.navy,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '13.5px',
                    px: '16px',
                    py: '9px',
                    borderRadius: colors.radiusBtn,
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background .15s',
                    '&:hover': { background: colors.navyDark },
                  }
            }
          >
            {action.icon}
            {action.label}
          </Box>
        ))}
      </Box>

      <Box
        onClick={onClear}
        sx={{
          color: colors.slate,
          fontWeight: 600,
          fontSize: '13px',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { color: colors.textSecondary },
        }}
      >
        {t('clear')}
      </Box>
    </Box>
  )
}
